import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowUpDown, ArrowUp, ArrowDown } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, money, fmtDate, fmtKST, fancallWhen } from "@/lib/api";
import { StatTile, StatusBadge, EmptyState } from "@/components/Bits";
import PaymentForm from "@/components/PaymentForm";
import { PastDueAlert } from "@/components/PastDueAlert";
import { DueSoonAlert } from "@/components/DueSoonAlert";
import { CollectNotice } from "@/components/CollectNotice";
import { FancallInfoBanner } from "@/components/FancallInfoBanner";
import SpecialClaimsList from "@/components/SpecialClaimsList";

const SHOW_PAID_KEY = "gh6_show_total_paid";

const ClaimsTile = ({ totals }) => (
  <div className="rounded-2xl border border-violet-500/20 bg-violet-500/[0.04] p-4 sm:p-5 col-span-2 lg:col-span-1" data-testid="stat-claims-box">
    <div className="flex items-baseline justify-between gap-2">
      <div className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold">Claims</div>
      <span className="text-[11px] text-zinc-500">paid</span>
    </div>
    <div data-testid="stat-claims" className="mt-2 font-mono text-xl sm:text-2xl font-bold">{totals.paid_claims}<span className="text-sm text-zinc-500 font-normal"> / {totals.claims - totals.pending_claims - totals.waitlist_claims - (totals.dropped_claims || 0)} placed</span></div>
    <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1">
      {Object.entries(totals.paid_by_category).map(([cat, n]) => (
        <div key={cat} className="flex justify-between text-xs">
          <span className="text-zinc-400">{cat}</span>
          <span data-testid={`stat-claims-${cat.toLowerCase().replace(/\s+/g, "-")}`} className={`font-mono font-semibold ${n ? "text-violet-200" : "text-zinc-600"}`}>{n}</span>
        </div>
      ))}
    </div>
  </div>
);

const BalanceRow = ({ label, value, testId }) => (
  <div className="flex items-baseline justify-between gap-2">
    <span className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold">{label}</span>
    <span data-testid={testId} className={`font-mono text-lg sm:text-xl font-bold ${value > 0 ? "text-pink-400" : "text-emerald-400"}`}>{money(value)}</span>
  </div>
);

const BalanceTile = ({ balance, special, onPay }) => (
  <div className="rounded-2xl border border-white/5 bg-zinc-900/60 p-4 sm:p-5 flex flex-col gap-1.5" data-testid="balance-tile">
    <BalanceRow label="Store balance" value={balance} testId="stat-balance" />
    <BalanceRow label="Special balance" value={special} testId="stat-special-balance" />
    {(balance > 0 || special > 0) && (
      <button type="button" data-testid="pay-balance-button" onClick={onPay} className="mt-auto pt-3 self-start">
        <span className="inline-flex items-center rounded-full bg-pink-500 px-3.5 py-1.5 text-xs font-semibold text-white transition-[transform,background-color] duration-200 hover:bg-pink-400 hover:-translate-y-0.5">Pay balance</span>
      </button>
    )}
  </div>
);

const TopBar = ({ totals, special, onPay }) => {
  const [showPaid, setShowPaid] = useState(() => localStorage.getItem(SHOW_PAID_KEY) === "1");
  const toggle = (v) => { setShowPaid(v); localStorage.setItem(SHOW_PAID_KEY, v ? "1" : "0"); };
  return (
    <>
      <label className="mt-8 inline-flex items-center gap-2 text-sm text-zinc-400 cursor-pointer select-none">
        <Checkbox data-testid="show-total-paid-checkbox" checked={showPaid} onCheckedChange={(c) => toggle(!!c)} />
        Show Total Paid
      </label>
      <div className={`grid grid-cols-2 gap-3 mt-3 ${showPaid ? "lg:grid-cols-5" : "lg:grid-cols-4"}`}>
        <div className="col-span-2 lg:col-span-1"><BalanceTile balance={totals.balance} special={special} onPay={onPay} /></div>
        <StatTile label="Pending review" value={money(totals.pending)} accent="text-amber-400" testId="stat-pending" />
        {showPaid && <StatTile label="Total paid" value={money(totals.paid)} accent="text-emerald-400" testId="stat-paid" />}
        <div className="rounded-2xl border border-white/5 bg-zinc-900/60 p-4 sm:p-5">
          <div className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold">Participation</div>
          <div data-testid="stat-participation" className="mt-2 font-mono text-xl sm:text-2xl font-bold text-sky-300">{totals.participation}%</div>
          <div data-testid="stat-participation-detail" className="text-xs text-zinc-500 mt-1">{totals.drops_joined} of {totals.drops_total} drops since joining · fancalls excluded</div>
        </div>
        <ClaimsTile totals={totals} />
      </div>
    </>
  );
};

const STATUS_LABEL = (c) => (c.status === "active" ? `Set #${c.set_number}` : c.status === "waitlist" ? "Waitlisted" : c.status === "dropped" ? `Dropped · Set #${c.set_number}` : c.status === "forfeited" ? "Dropped · unpaid" : "Pending");
const mmdd = (iso) => new Date(iso).toLocaleDateString("en-US", { timeZone: "Asia/Seoul", month: "2-digit", day: "2-digit" });
const KSTDate = ({ iso }) => (iso ? <><span className="sm:hidden">{mmdd(iso)}</span><span className="hidden sm:inline">{fmtKST(iso)}</span></> : "—");
const dueSoon = (c) => {
  const ms = new Date(c.payment_due) - Date.now();
  return !!c.payment_due && !c.paid && c.status !== "dropped" && c.status !== "forfeited" && ms > 0 && ms <= 86400000;
};

const COLUMNS = [
  { key: "store", label: "Store Name", value: (c) => c.store.toLowerCase() },
  { key: "round_type", label: "Round Type", value: (c) => c.round_type },
  { key: "album_type", label: "Album Type", value: (c) => c.album_type },
  { key: "member", label: "Member Claimed", value: (c) => c.member },
  { key: "status", label: "Claim Status", value: (c) => (c.status === "active" ? c.set_number : c.status === "pending" ? 10000 : c.status === "dropped" ? 15000 : c.status === "forfeited" ? 17000 : 20000) },
  { key: "claims_close", label: "Drop Close Date", value: (c) => c.claims_close || "" },
  { key: "payment_due", label: "Payment Due Date", value: (c) => c.payment_due || "" },
  { key: "paid", label: "Payment", value: (c) => (c.paid ? 0 : 1) },
  { key: "price", label: "Amount", value: (c) => c.price, right: true },
];

const SortHeader = ({ col, sort, onSort }) => {
  const active = sort.key === col.key;
  const Icon = !active ? ArrowUpDown : sort.dir === 1 ? ArrowUp : ArrowDown;
  return (
    <th className={`px-3 py-2.5 font-semibold ${col.right ? "text-right" : "text-left"}`}>
      <button type="button" data-testid={`claims-sort-${col.key}`} onClick={() => onSort(col.key)} className={`inline-flex items-center gap-1 whitespace-nowrap transition-colors hover:text-white ${active ? "text-violet-200" : ""}`}>
        {col.label} <Icon className="h-3 w-3" />
      </button>
    </th>
  );
};

const FILTERS = [["all", "All"], ["unpaid", "Unpaid"], ["waitlist", "Waitlisted"]];
const ALL_STORES = "__all__";

const ClaimFilters = ({ status, setStatus, store, setStore, stores, shown, total }) => (
  <div className="flex flex-wrap items-center gap-2 mb-3" data-testid="claims-filters">
    {FILTERS.map(([k, l]) => (
      <button key={k} type="button" data-testid={`claims-filter-${k}`} aria-pressed={status === k} onClick={() => setStatus(k)}
        className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors duration-200 ${status === k ? "border-violet-400 bg-violet-500/15 text-violet-100" : "border-zinc-700 text-zinc-400 hover:border-zinc-500 hover:text-white"}`}>
        {l}
      </button>
    ))}
    <Select value={store} onValueChange={setStore}>
      <SelectTrigger data-testid="claims-filter-store" className="h-8 w-auto min-w-[150px] rounded-full bg-zinc-950 text-xs"><SelectValue /></SelectTrigger>
      <SelectContent>
        <SelectItem value={ALL_STORES}>All stores</SelectItem>
        {stores.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
      </SelectContent>
    </Select>
    <span data-testid="claims-filter-count" className="ml-auto font-mono text-xs text-zinc-500">{shown} of {total} claims</span>
  </div>
);

const ClaimsList = ({ claims }) => {
  const [sort, setSort] = useState({ key: "store", dir: 1 });
  const [status, setStatus] = useState("all");
  const [store, setStore] = useState(ALL_STORES);
  const stores = useMemo(() => [...new Set(claims.map((c) => c.store))].sort((a, b) => a.localeCompare(b)), [claims]);
  const rows = useMemo(() => {
    const col = COLUMNS.find((c) => c.key === sort.key);
    return claims
      .filter((c) => (status === "unpaid" ? !c.paid && c.status !== "dropped" : status === "waitlist" ? c.status === "waitlist" : true) && (store === ALL_STORES || c.store === store))
      .sort((a, b) => {
        const x = col.value(a), y = col.value(b);
        return (x < y ? -1 : x > y ? 1 : 0) * sort.dir;
      });
  }, [claims, sort, status, store]);
  const onSort = (key) => setSort((s) => ({ key, dir: s.key === key ? -s.dir : 1 }));
  if (claims.length === 0) return <EmptyState testId="claims-empty" title="No claims yet" hint="Head to Open Drops to claim a member." />;
  return (
    <>
    <ClaimFilters status={status} setStatus={setStatus} store={store} setStore={setStore} stores={stores} shown={rows.length} total={claims.length} />
    {rows.length === 0 ? <EmptyState testId="claims-filter-empty" title="No claims match these filters" /> : (
    <div className="overflow-x-auto rounded-2xl border border-white/5 bg-zinc-900/60" data-testid="claims-table">
      <table className="w-full min-w-[840px] sm:min-w-[1040px] text-sm">
        <thead className="text-[11px] uppercase tracking-widest text-zinc-500 border-b border-white/5">
          <tr>{COLUMNS.map((col) => <SortHeader key={col.key} col={col} sort={sort} onSort={onSort} />)}</tr>
        </thead>
        <tbody>
          {rows.map((c) => (
            <tr key={c.id} data-testid={`claim-item-${c.id}`} className="border-b border-white/5 last:border-0 transition-colors hover:bg-white/[0.02]">
              <td className="px-3 py-2.5"><Link to={`/drops/${c.drop_id}`} className="font-semibold hover:text-violet-300 transition-colors">{c.store}</Link></td>
              <td className="px-3 py-2.5 text-zinc-300 whitespace-nowrap">{c.round_type}</td>
              <td className="px-3 py-2.5 text-zinc-300 whitespace-nowrap">{c.album_type}</td>
              <td className="px-3 py-2.5"><span className="rounded-lg bg-violet-500/15 text-violet-200 px-2 py-0.5 font-semibold">{c.member}</span></td>
              <td className="px-3 py-2.5 whitespace-nowrap" data-testid={`claim-status-${c.id}`}>
                {c.status === "active" ? <span className="font-mono text-xs text-zinc-300">{STATUS_LABEL(c)}</span> : <StatusBadge status={c.status} label={STATUS_LABEL(c)} />}
              </td>
              <td className="px-3 py-2.5 whitespace-nowrap text-xs text-zinc-300" data-testid={`claim-close-date-${c.id}`}><KSTDate iso={c.claims_close} /></td>
              <td className="px-3 py-2.5 whitespace-nowrap text-xs text-zinc-300" data-testid={`claim-payment-due-${c.id}`}>
                <span data-testid={dueSoon(c) ? `claim-due-soon-${c.id}` : undefined}
                  className={dueSoon(c) ? "rounded-md bg-amber-400/15 px-1.5 py-0.5 font-semibold text-amber-200 shadow-[0_0_12px_rgba(251,191,36,0.45)] ring-1 ring-amber-400/50 animate-pulse" : ""}>
                  <KSTDate iso={c.payment_due} />
                </span>
                {c.pay_by && c.status === "active" && !c.paid && <div data-testid={`claim-pay-by-${c.id}`} className="text-[11px] text-amber-300">{c.restored_at ? "Restored" : c.collected_set ? "Collected early" : "Moved up"}</div>}
              </td>
              <td className="px-3 py-2.5" data-testid={`claim-paid-${c.id}`}>{c.status === "dropped" || c.status === "forfeited" ? <span className="text-xs text-zinc-500">Not charged</span> : <StatusBadge status={c.paid ? "approved" : "pending"} label={c.paid ? "Paid" : "Unpaid"} />}</td>
              <td className={`px-3 py-2.5 text-right font-mono ${c.status === "dropped" || c.status === "forfeited" ? "text-zinc-600 line-through" : ""}`}>
                {money(c.status === "forfeited" ? c.base_price : c.price)}
                {c.late_fee > 0 && c.status === "active" && <div data-testid={`claim-late-fee-${c.id}`} className="text-[11px] text-red-300 no-underline">incl. {money(c.late_fee)} late fee</div>}
                {c.fee_waived && c.status === "active" && <div data-testid={`claim-fee-waived-${c.id}`} className="text-[11px] text-emerald-300 no-underline">Late fee waived</div>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
    )}
    </>
  );
};

const PaymentsList = ({ payments }) =>
  payments.length === 0 ? (
    <EmptyState testId="payments-empty" title="No payments submitted" hint="Use the Submit payment tab once you've paid." />
  ) : (
    <div className="space-y-2">
      {payments.map((p) => (
        <div key={p.id} data-testid={`payment-item-${p.id}`} className="rounded-xl border border-white/5 bg-zinc-900/60 px-4 py-3">
          <div className="flex items-center gap-3">
            <span className="font-mono font-bold">{money(p.amount)}</span>
            {p.kind === "special" && <span data-testid={`payment-special-tag-${p.id}`} className="rounded-full bg-amber-400/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-amber-200">Special</span>}
            <span className="text-sm text-zinc-400 truncate flex-1">{p.drop_title || "General"}{p.methods?.length ? ` · ${p.methods.map((m) => m.split(":")[0]).join(", ")}` : ""}{p.reference ? ` · ref ${p.reference}` : ""}</span>
            <StatusBadge status={p.status} testId={`payment-status-${p.id}`} />
          </div>
          <div className="text-xs text-zinc-500 mt-1">{fmtDate(p.created_at)}{p.admin_note ? ` · Admin: ${p.admin_note}` : ""}</div>
        </div>
      ))}
    </div>
  );

const RafflesList = ({ raffles }) =>
  raffles.length === 0 ? (
    <EmptyState testId="raffles-empty" title="No raffle entries yet" />
  ) : (
    <div className="space-y-2">
      {raffles.map((r) => (
        <div key={r.raffle_id} data-testid={`joiner-raffle-${r.raffle_id}`} className="flex items-center gap-4 rounded-xl border border-white/5 bg-zinc-900/60 px-4 py-3">
          <div className="min-w-0 flex-1">
            <div className="font-semibold truncate">{r.name}</div>
            <div className="text-xs text-zinc-500 truncate">{fancallWhen(r) || "Fancall date TBA"}</div>
          </div>
          <span className="font-mono text-sm">{r.entries}/{r.is_unit ? 4 : 10} {r.entries === 1 ? "entry" : "entries"}</span>
          {r.won && <StatusBadge status="won" label="Winner" testId={`joiner-raffle-won-${r.raffle_id}`} />}
        </div>
      ))}
    </div>
  );

const InclusionsList = ({ inclusions }) =>
  inclusions.length === 0 ? (
    <EmptyState testId="inclusions-empty" title="No inclusions logged yet" />
  ) : (
    <div className="space-y-2">
      {inclusions.map((i) => (
        <div key={i.id} data-testid={`joiner-inclusion-${i.id}`} className="flex items-center gap-4 rounded-xl border border-white/5 bg-zinc-900/60 px-4 py-3">
          <div className="min-w-0 flex-1">
            <div className="font-semibold truncate">{i.item}{i.quantity > 1 ? ` ×${i.quantity}` : ""}</div>
            <div className="text-xs text-zinc-500 truncate">{i.drop_title}{i.set_number ? ` · Set #${i.set_number}` : ""}</div>
          </div>
          <StatusBadge status={i.status} label={i.status === "sent" ? "Sent" : "Pending"} />
        </div>
      ))}
    </div>
  );

export default function JoinerProfile() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [tab, setTab] = useState("claims");
  const [payFor, setPayFor] = useState("");
  const openPay = (key = "") => { setPayFor(key); setTab("submit"); };
  const load = useCallback(() => {
    api.get(`/joiners/${id}`).then((r) => setData(r.data)).catch(() => setError(true));
  }, [id]);
  useEffect(() => {
    load();
  }, [load]);

  if (error) return <EmptyState testId="joiner-not-found" title="Joiner not found" />;
  if (!data) return <div className="text-zinc-500 text-sm">Loading…</div>;
  const { joiner, totals } = data;

  return (
    <div className="rise">
      <Link to="/joiners" data-testid="back-to-joiners" className="inline-flex items-center gap-1.5 text-sm text-zinc-400 hover:text-white transition-colors">
        <ArrowLeft className="h-4 w-4" /> All joiners
      </Link>
      <h1 data-testid="joiner-name" className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight mt-4">{joiner.name}</h1>
      {(joiner.handle || joiner.contact) && <p className="text-zinc-400 mt-1">{[joiner.handle, joiner.contact].filter(Boolean).join(" · ")}</p>}

      <PastDueAlert dropBalances={data.drop_balances} specialBalances={data.special_balances} onPay={openPay} />
      <DueSoonAlert dropBalances={data.drop_balances} specialBalances={data.special_balances} onPay={openPay} />
      <CollectNotice dropBalances={data.drop_balances} onPay={openPay} />
      <FancallInfoBanner raffles={data.raffles} joinerId={id} onDone={load} />
      <TopBar totals={totals} special={data.special_totals?.balance || 0} onPay={() => openPay()} />
      {totals.pending_claims > 0 && (
        <p className="text-xs text-amber-300/80 mt-3" data-testid="pending-claims-note">
          {totals.pending_claims} pending claim{totals.pending_claims > 1 ? "s" : ""} not charged yet. They become active when their set opens.
        </p>
      )}

      <Tabs value={tab} onValueChange={(t) => { setPayFor(""); setTab(t); }} className="mt-10">
        <TabsList className="bg-zinc-900 border border-white/5 h-11 p-1 rounded-xl max-w-full overflow-x-auto justify-start">
          <TabsTrigger value="claims" data-testid="tab-claims" className="rounded-lg">Claims</TabsTrigger>
          <TabsTrigger value="special" data-testid="tab-special-claims" className="rounded-lg">Special Claims</TabsTrigger>
          <TabsTrigger value="submit" data-testid="tab-submit-payment" className="rounded-lg">Payment Proof</TabsTrigger>
          <TabsTrigger value="payments" data-testid="tab-payments" className="rounded-lg">Payments</TabsTrigger>
          <TabsTrigger value="inclusions" data-testid="tab-inclusions" className="rounded-lg">Inclusions</TabsTrigger>
          <TabsTrigger value="raffles" data-testid="tab-raffles" className="rounded-lg">Raffles</TabsTrigger>
        </TabsList>
        <TabsContent value="claims" className="mt-5"><ClaimsList claims={data.claims} /></TabsContent>
        <TabsContent value="special" className="mt-5"><SpecialClaimsList claims={data.special_claims} totals={data.special_totals} balances={data.special_balances} onPay={(sdId) => openPay(`s:${sdId}`)} /></TabsContent>
        <TabsContent value="payments" className="mt-5"><PaymentsList payments={data.payments} /></TabsContent>
        <TabsContent value="raffles" className="mt-5"><RafflesList raffles={data.raffles} /></TabsContent>
        <TabsContent value="inclusions" className="mt-5">
          <Link to={`/inclusions/${id}`} data-testid="joiner-inclusion-breakdown-link" className="inline-flex items-center gap-1 text-sm text-pink-300 hover:text-pink-200 mb-3">View inclusion count breakdown →</Link>
          <InclusionsList inclusions={data.inclusions} />
        </TabsContent>
        <TabsContent value="submit" className="mt-5">
          <PaymentForm joinerId={id} dropBalances={data.drop_balances} specialBalances={data.special_balances} initialDrop={payFor} onDone={() => { load(); setTab("payments"); }} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
